import { useEffect, useMemo, useState } from 'react'
import Swal from 'sweetalert2'
import IPButton from '../../../../components/IPButton'
import IPInput from '../../../../components/IPInput'
import IPModal from '../../../../components/IPModal'
import IPSelectBox from '../../../../components/IPSelectBox'
import { ISelectOption } from '../../../../components/IPSelectBox/IPSelectBox'
import ExcelButton from '../../../../components/IPExcelButton/IPExcelButton'
import InputCampaign from '../../../../components/InputCampaign'
import { BaseComponentWithContextProps } from '../../../../base/customHooks/componentHOC'
import { IException } from '../../../../base/models'
import { exportToXLSX } from '../../../../base/utils/dataHelper'
import { DateInputValue, toApiDate, toDateInputValue, validateDateRange } from '../models/dateInput'
import {
  appleDepStatusClass,
  AppleDepStatusLabels,
  IAppleDepCustomer,
  IAppleDepDevice,
  IAppleDepEnvironment,
} from '../models/models'
import {
  AssignAppleDepCustomerApi,
  ClearAppleDepCustomerApi,
  GetAppleDepCustomersApi,
  GetAppleDepDevicesApi,
  SendToAppleApi,
} from '../services/appleDepApi'
import ExcelImportModal from './ExcelImportModal'

interface DevicesTabProps extends BaseComponentWithContextProps {
  environment?: IAppleDepEnvironment
  /** Müşteri listesi değiştiğinde artan sayaç; listeyi yeniden yükler. */
  customerVersion: number
  onGoToCustomers: () => void
  onSent: () => void
}

const startOfMonth = () => {
  const date = new Date()
  date.setDate(1)
  date.setHours(0, 0, 0, 0)
  return date
}


const formatDisplayDate = (value?: string) => {
  if (!value) return '-'
  const date = new Date(value)
  return isNaN(date.getTime()) ? value : date.toLocaleDateString('tr-TR')
}

/**
 * Cihazlarım sekmesi. SAP'ten gelen (Index'in bayiye sattığı) Apple cihazları
 * listelenir; bayi seçtiği cihazlara müşteri atar ve Apple'a gönderir.
 */
export default function DevicesTab(props: DevicesTabProps) {
  const [startDate, setStartDate] = useState<DateInputValue>(startOfMonth())
  const [endDate, setEndDate] = useState<DateInputValue>(new Date())
  const [serialNumber, setSerialNumber] = useState('')
  const [invoiceNo, setInvoiceNo] = useState('')
  const [statusFilter, setStatusFilter] = useState('')

  const [devices, setDevices] = useState<IAppleDepDevice[]>([])
  const [customers, setCustomers] = useState<IAppleDepCustomer[]>([])
  const [selectedIds, setSelectedIds] = useState<number[]>([])

  const [showAssignModal, setShowAssignModal] = useState(false)
  const [showImportModal, setShowImportModal] = useState(false)
  const [useDealerOwnOrg, setUseDealerOwnOrg] = useState(false)
  const [assignCustomerId, setAssignCustomerId] = useState<number>(0)
  const [dealerOrderNumber, setDealerOrderNumber] = useState('')
  const [dealerOrderDate, setDealerOrderDate] = useState(toDateInputValue(new Date()))
  const [poNumber, setPoNumber] = useState('')

  const requiresDealerOrder = props.environment?.OrderNumberSource !== 'Sap'
  const canUseDealerOwn = props.environment?.CanUseDealerOwnOrganization ?? false
  const canUseEndCustomer = props.environment?.CanUseEndCustomerOrganization ?? true
  // Yalnızca kendi kurumu modunda seçenek sunulmaz, doğrudan uygulanır.
  const dealerOwnForced = canUseDealerOwn && !canUseEndCustomer

  const showError = (err: any, fallback = 'İşlem sırasında bir hata oluştu.') => {
    const exception = err as IException
    Swal.fire({
      icon: 'error',
      title: exception?.description ?? fallback,
      showConfirmButton: true,
      allowOutsideClick: false,
    })
  }

  const loadCustomers = async () => {
    try {
      const api: GetAppleDepCustomersApi = new GetAppleDepCustomersApi({})
      setCustomers(await api.getCustomers())
    } catch (err: any) {
      showError(err, 'Müşteri listesi alınamadı.')
    }
  }

  const loadDevices = async (refreshFromSap = true) => {
    const dateError = validateDateRange(startDate, endDate)
    if (dateError) {
      Swal.fire({ icon: 'warning', title: dateError })
      return
    }

    props.showLoading()
    try {
      const api: GetAppleDepDevicesApi = new GetAppleDepDevicesApi({})
      const result = await api.getDevices({
        StartDate: toApiDate(startDate),
        EndDate: toApiDate(endDate),
        SerialNumber: serialNumber,
        InvoiceNo: invoiceNo,
        Status: statusFilter,
        RefreshFromSap: refreshFromSap,
      })
      setDevices(result ?? [])
      setSelectedIds([])
    } catch (err: any) {
      showError(err, 'Cihaz listesi alınamadı.')
    } finally {
      props.hideLoading()
    }
  }

  useEffect(() => {
    loadCustomers()
    loadDevices(true)
    // İlk yüklemede bir kez çalışır; sonraki yenilemeler Listele butonuyla yapılır.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (props.customerVersion > 0) loadCustomers()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.customerVersion])

  const customerOptions: ISelectOption[] = useMemo(
    () =>
      customers
        .filter(c => c.Active)
        .map(c => ({ value: c.Id, label: `${c.CustomerName} — ${c.OrganizationId}` })),
    [customers]
  )

  const selectedDevices = useMemo(
    () => devices.filter(d => selectedIds.includes(d.Id)),
    [devices, selectedIds]
  )

  const sendableSelected = useMemo(() => selectedDevices.filter(d => d.CanSend), [selectedDevices])

  const toggleDevice = (id: number) => {
    setSelectedIds(current => (current.includes(id) ? current.filter(x => x !== id) : [...current, id]))
  }

  const toggleAll = () => {
    setSelectedIds(current => (current.length === devices.length ? [] : devices.map(d => d.Id)))
  }

  const openAssignModal = () => {
    if (selectedIds.length === 0) {
      Swal.fire({ icon: 'warning', title: 'Önce cihaz seçin.' })
      return
    }

    // Yalnızca kendi kurumu modunda müşteri tanımı gerekmez.
    if (customerOptions.length === 0 && !canUseDealerOwn) {
      Swal.fire({
        icon: 'warning',
        title: 'Tanımlı müşteriniz yok.',
        text: 'Önce Müşterilerim sekmesinden müşterinizi ve Organization ID bilgisini tanımlayın.',
        confirmButtonText: 'Müşterilerim sekmesine git',
      }).then(() => props.onGoToCustomers())
      return
    }

    setUseDealerOwnOrg(dealerOwnForced)

    // Seçili cihazlardan birinde daha önce girilmiş bilgi varsa forma taşı.
    const withOrder = selectedDevices.find(d => d.DealerOrderNumber)
    setAssignCustomerId(selectedDevices.find(d => d.AppleDepCustomerId)?.AppleDepCustomerId ?? 0)
    setDealerOrderNumber(withOrder?.DealerOrderNumber ?? '')
    setDealerOrderDate(withOrder?.DealerOrderDate ? toDateInputValue(new Date(withOrder.DealerOrderDate)) : toDateInputValue(new Date()))
    setPoNumber(selectedDevices.find(d => d.PoNumber)?.PoNumber ?? '')
    setShowAssignModal(true)
  }

  const assignCustomer = async () => {
    if (!useDealerOwnOrg && !assignCustomerId) {
      Swal.fire({ icon: 'warning', title: 'Müşteri seçin.' })
      return
    }

    if (requiresDealerOrder && !dealerOrderNumber.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Sipariş/fatura numarası zorunludur.',
        text: 'Apple, cihazı son kullanıcıya satan sipariş numarasını istiyor.',
      })
      return
    }

    props.showLoading()
    try {
      const api: AssignAppleDepCustomerApi = new AssignAppleDepCustomerApi({})
      const result = await api.assignCustomer({
        DeviceIds: selectedIds,
        AppleDepCustomerId: assignCustomerId,
        UseDealerOwnOrganization: useDealerOwnOrg,
        DealerOrderNumber: dealerOrderNumber,
        DealerOrderDate: dealerOrderDate,
        PoNumber: poNumber,
      })

      setShowAssignModal(false)
      await loadDevices(false)

      Swal.fire({
        icon: result?.BlockedDevices?.length ? 'warning' : 'success',
        title: 'Müşteri atandı',
        text: result?.Message,
      })
    } catch (err: any) {
      showError(err, 'Müşteri atanamadı.')
    } finally {
      props.hideLoading()
    }
  }

  const clearCustomer = async () => {
    if (selectedIds.length === 0) {
      Swal.fire({ icon: 'warning', title: 'Önce cihaz seçin.' })
      return
    }

    props.showLoading()
    try {
      const api: ClearAppleDepCustomerApi = new ClearAppleDepCustomerApi({})
      await api.clearCustomer(selectedIds)
      await loadDevices(false)
    } catch (err: any) {
      showError(err, 'Müşteri ataması kaldırılamadı.')
    } finally {
      props.hideLoading()
    }
  }

  const sendToApple = async () => {
    if (sendableSelected.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Gönderilebilecek cihaz seçilmedi.',
        text: 'Cihazın gönderilebilmesi için müşteri ataması yapılmış ve daha önce Apple\'a kaydedilmemiş olması gerekir.',
      })
      return
    }

    const isLive = props.environment?.IsLive === 1
    const confirmation = await Swal.fire({
      icon: 'question',
      title: `${sendableSelected.length} cihaz Apple'a gönderilecek`,
      html: `
        <div style="text-align:left">
          <p><strong>Ortam:</strong> ${isLive ? 'CANLI' : 'TEST'}</p>
          <p><strong>İşlem tipi:</strong> Kayıt (OR)</p>
          <p>Gönderim sonrası cihazların Apple'daki durumu asenkron olarak güncellenir.</p>
        </div>`,
      showCancelButton: true,
      confirmButtonText: "Apple'a Gönder",
      cancelButtonText: 'Vazgeç',
    })

    if (!confirmation.isConfirmed) return

    props.showLoading()
    try {
      const api: SendToAppleApi = new SendToAppleApi({})
      const result = await api.sendToApple({
        DeviceIds: sendableSelected.map(d => d.Id),
        OrderType: 'OR',
      })

      await loadDevices(false)

      const skipped = result?.SkippedDevices ?? []
      await Swal.fire({
        icon: 'success',
        title: 'Gönderim yapıldı',
        html: `
          <div style="text-align:left">
            <p><strong>İşlem No:</strong> ${result?.TransactionId ?? '-'}</p>
            <p><strong>Apple İşlem No:</strong> ${result?.DeviceEnrollmentTransactionId ?? 'bekleniyor'}</p>
            <p><strong>Gönderilen cihaz:</strong> ${result?.SentDeviceCount ?? 0}</p>
            ${skipped.length ? `<p><strong>Atlanan:</strong> ${skipped.length}</p>` : ''}
          </div>`,
      })

      props.onSent()
    } catch (err: any) {
      showError(err, "Apple'a gönderim başarısız oldu.")
    } finally {
      props.hideLoading()
    }
  }

  const exportToExcel = () => {
    if (devices.length === 0) {
      Swal.fire({ icon: 'warning', title: 'Aktarılacak kayıt yok.' })
      return
    }

    // Seçim varsa yalnızca seçili cihazlar aktarılır.
    const rows = (selectedIds.length > 0 ? selectedDevices : devices).map(device => ({
      'Seri No / IMEI': device.SerialNumber,
      'Ürün Kodu': device.ProductCode ?? '',
      Ürün: device.ProductDescription ?? '',
      'Sipariş No': device.SapOrderNo ?? '',
      'Sipariş Tarihi': device.SapOrderDate ? formatDisplayDate(device.SapOrderDate) : '',
      'Teslimat No': device.SapDeliveryNo ?? '',
      'Teslimat Tarihi': device.SapDeliveryDate ? formatDisplayDate(device.SapDeliveryDate) : '',
      'Fatura No': device.SapInvoiceNo ?? '',
      'Fatura Tarihi': device.SapInvoiceDate ? formatDisplayDate(device.SapInvoiceDate) : '',
      Müşteri: device.CustomerName ?? '',
      'Organization ID': device.OrganizationId ?? '',
      'Bayi Sipariş No': device.DealerOrderNumber ?? '',
      'Satış Tarihi': device.DealerOrderDate ? formatDisplayDate(device.DealerOrderDate) : '',
      'PO No': device.PoNumber ?? '',
      Durum: AppleDepStatusLabels[device.Status] ?? device.Status,
      Açıklama: device.StatusMessage ?? device.BlockReason ?? '',
      'Apple Kayıt Tarihi': device.EnrolledOn ? formatDisplayDate(device.EnrolledOn) : '',
    }))

    exportToXLSX(rows, 'Apple DEP Cihazlarim')
  }

  const statusOptions: ISelectOption[] = [
    { value: '', label: 'Tümü' },
    ...Object.keys(AppleDepStatusLabels).map(key => ({ value: key, label: AppleDepStatusLabels[key] })),
  ]

  return (
    <div className='appleDepDevices'>
      <div className='appleDepFilters'>
        <div className='row'>
          <div className='col-12 col-md-2 mb-2'>
            <InputCampaign
              type='date'
              id='AppleDepStartDate'
              name='AppleDepStartDate'
              label='Fatura Başlangıç'
              value={toDateInputValue(startDate)}
              onChange={e => setStartDate(e.target.value)}
              isActive
            />
          </div>
          <div className='col-12 col-md-2 mb-2'>
            <InputCampaign
              type='date'
              id='AppleDepEndDate'
              name='AppleDepEndDate'
              label='Fatura Bitiş'
              value={toDateInputValue(endDate)}
              onChange={e => setEndDate(e.target.value)}
              isActive
            />
          </div>
          <div className='col-12 col-md-2 mb-2'>
            <label className='labeltext'>Seri No / IMEI</label>
            <IPInput type='text' value={serialNumber} onChange={e => setSerialNumber(e.target.value)} />
          </div>
          <div className='col-12 col-md-2 mb-2'>
            <label className='labeltext'>Fatura No</label>
            <IPInput type='text' value={invoiceNo} onChange={e => setInvoiceNo(e.target.value)} />
          </div>
          <div className='col-12 col-md-2 mb-2'>
            <label className='labeltext'>Durum</label>
            <IPSelectBox
              isClearable
              options={statusOptions}
              value={statusOptions.find(o => o.value === statusFilter)}
              onChangeSingle={(option: ISelectOption) => setStatusFilter(option ? `${option.value}` : '')}
            />
          </div>
          <div className='col-12 col-md-2 mb-2 appleDepFilterAction'>
            <IPButton text='Listele' className='appleDepPrimaryButton' onClick={() => loadDevices(true)} />
          </div>
        </div>
      </div>

      <div className='appleDepToolbar'>
        <div className='appleDepToolbarInfo'>
          {devices.length} cihaz listelendi, <strong>{selectedIds.length}</strong> seçili
          {selectedIds.length > 0 && sendableSelected.length !== selectedIds.length && (
            <span className='appleDepToolbarWarn'> ({sendableSelected.length} tanesi gönderilebilir)</span>
          )}
        </div>
        <div className='appleDepToolbarButtons'>
          <IPButton
            text='Excel ile Yükle'
            className='appleDepGhostButton'
            onClick={() => setShowImportModal(true)}
          />
          <IPButton text='Müşteri Ata' className='appleDepSecondaryButton' onClick={openAssignModal} />
          <IPButton text='Atamayı Kaldır' className='appleDepGhostButton' onClick={clearCustomer} />
          <IPButton
            text="Apple'a Gönder"
            className='appleDepPrimaryButton'
            onClick={sendToApple}
            disabled={props.environment ? !props.environment.DealerCanSend : false}
          />
          <ExcelButton onClick={exportToExcel} />
        </div>
      </div>

      {devices.length === 0 ? (
        <div className='appleDepEmpty'>
          Seçilen tarih aralığında Apple cihazı bulunamadı. Tarih aralığını genişletip tekrar deneyin.
        </div>
      ) : (
        <div className='appleDepTableWrapper'>
          <table className='table table-hover appleDepTable'>
            <thead>
              <tr>
                <th className='appleDepCheckCol'>
                  <input
                    type='checkbox'
                    checked={selectedIds.length === devices.length && devices.length > 0}
                    onChange={toggleAll}
                  />
                </th>
                <th>Seri No / IMEI</th>
                <th>Ürün</th>
                <th>Sipariş No</th>
                <th>Teslimat No</th>
                <th>Fatura No</th>
                <th>Fatura Tarihi</th>
                <th>Müşteri</th>
                <th>Organization ID</th>
                <th>Bayi Sipariş No</th>
                <th>Durum</th>
              </tr>
            </thead>
            <tbody>
              {devices.map(device => (
                <tr key={device.Id} className={selectedIds.includes(device.Id) ? 'appleDepRowSelected' : ''}>
                  <td className='appleDepCheckCol'>
                    <input
                      type='checkbox'
                      checked={selectedIds.includes(device.Id)}
                      onChange={() => toggleDevice(device.Id)}
                    />
                  </td>
                  <td>
                    <strong>{device.SerialNumber}</strong>
                  </td>
                  <td>
                    <div className='appleDepProductName'>{device.ProductDescription}</div>
                    <small>{device.ProductCode}</small>
                  </td>
                  <td>
                    <strong>{device.SapOrderNo ?? '-'}</strong>
                  </td>
                  <td>{device.SapDeliveryNo ?? '-'}</td>
                  <td>{device.SapInvoiceNo}</td>
                  <td>{formatDisplayDate(device.SapInvoiceDate)}</td>
                  <td>{device.CustomerName ?? '-'}</td>
                  <td>{device.OrganizationId ?? '-'}</td>
                  <td>{device.DealerOrderNumber ?? '-'}</td>
                  <td>
                    <span className={appleDepStatusClass(device.Status)}>
                      {AppleDepStatusLabels[device.Status] ?? device.Status}
                    </span>
                    {device.BlockReason && <div className='appleDepBlockReason'>{device.BlockReason}</div>}
                    {device.StatusMessage && <div className='appleDepBlockReason'>{device.StatusMessage}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <IPModal
        show={showAssignModal}
        onClose={() => setShowAssignModal(false)}
        onConfirm={assignCustomer}
        confirmText='Kaydet'
        width={720}
        title={`Müşteri Ata (${selectedIds.length} cihaz)`}
      >
        <div className='appleDepForm'>
          {canUseDealerOwn && canUseEndCustomer && (
            <div className='form-row'>
              <div className='form-group col-md-12'>
                <label className='labeltext'>Cihazlar hangi Apple Business Manager hesabına kaydedilsin?</label>
                <div className='appleDepRadioGroup'>
                  <label>
                    <input
                      type='radio'
                      name='appleDepOrgSource'
                      checked={!useDealerOwnOrg}
                      onChange={() => setUseDealerOwnOrg(false)}
                    />{' '}
                    Son kullanıcımın hesabına
                  </label>
                  <label>
                    <input
                      type='radio'
                      name='appleDepOrgSource'
                      checked={useDealerOwnOrg}
                      onChange={() => setUseDealerOwnOrg(true)}
                    />{' '}
                    Kendi kurumuma
                    {props.environment?.DealerOwnOrganizationId
                      ? ` (${props.environment.DealerOwnOrganizationId})`
                      : ''}
                  </label>
                </div>
              </div>
            </div>
          )}

          {dealerOwnForced && (
            <div className='appleDepInfoBox'>
              Cihazlar <strong>kendi kurumunuzun</strong> Apple Business Manager hesabına kaydedilecek
              {props.environment?.DealerOwnOrganizationId
                ? ` (Organization ID: ${props.environment.DealerOwnOrganizationId})`
                : ''}
              .
            </div>
          )}

          {!useDealerOwnOrg && (
            <div className='form-row'>
              <div className='form-group col-md-12'>
                <label className='labeltext'>Müşteri (Organization ID) *</label>
                <IPSelectBox
                  options={customerOptions}
                  value={customerOptions.find(o => o.value === assignCustomerId)}
                  onChangeSingle={(option: ISelectOption) => setAssignCustomerId(option ? Number(option.value) : 0)}
                />
              </div>
            </div>
          )}
          <div className='form-row'>
            <div className='form-group col-md-6'>
              <label className='labeltext'>
                Bayi Sipariş / Fatura No {requiresDealerOrder ? '*' : ''}
              </label>
              <IPInput
                type='text'
                maxLength={32}
                value={dealerOrderNumber}
                onChange={e => setDealerOrderNumber(e.target.value)}
              />
              <small className='appleDepHint'>Cihazı son kullanıcıya sattığınız sipariş/fatura numarası</small>
            </div>
            <div className='form-group col-md-6'>
              <label className='labeltext'>Satış Tarihi {requiresDealerOrder ? '*' : ''}</label>
              <IPInput type='date' value={dealerOrderDate} onChange={e => setDealerOrderDate(e.target.value)} />
            </div>
          </div>
          <div className='form-row'>
            <div className='form-group col-md-6'>
              <label className='labeltext'>Müşteri PO No</label>
              <IPInput type='text' maxLength={100} value={poNumber} onChange={e => setPoNumber(e.target.value)} />
            </div>
          </div>

          <div className='appleDepInfoBox'>
            Apple'a gönderilen <strong>sipariş numarası</strong> (SAP SIPARIS_NO),{' '}
            <strong>teslimat numarası</strong> (SAP TESLIMAT_NO) ve <strong>seri numarası</strong>{' '}
            Index'in SAP kaydından otomatik alınır; elle değiştirilemez. Buradaki bayi sipariş bilgisi
            yalnızca kendi kaydınız için tutulur.
          </div>
        </div>
      </IPModal>

      <ExcelImportModal
        show={showImportModal}
        environment={props.environment}
        showLoading={props.showLoading}
        hideLoading={props.hideLoading}
        onClose={() => setShowImportModal(false)}
        onImported={() => {
          loadCustomers()
          loadDevices(false)
        }}
      />
    </div>
  )
}
